import { prettyDateTime } from "@app/date";
import {
  type DatabaseDependency,
  type DeployDatabaseRow,
  fetchCostsByDatabases,
  fetchDatabaseImageById,
  fetchDatabaseImages,
  fetchDatabases,
  fetchEnvironmentById,
  fetchEnvironments,
  fetchManualScaleRecommendations,
  fetchServices,
  hasDeployDatabaseImage,
  selectDatabaseImageById,
  selectDatabasesByEnvIdForTable,
  selectDatabasesForTable,
  selectLatestOpByDatabaseId,
  selectServiceById,
} from "@app/deploy";
import { selectOrganizationSelectedId } from "@app/organizations";
import { useCompositeLoader, useQuery, useSelector } from "@app/react";
import {
  createDbUrl,
  databaseDetailUrl,
  databaseScaleUrl,
  environmentCreateDbUrl,
  operationDetailUrl,
} from "@app/routes";
import { capitalize } from "@app/string-utils";
import type { DeployDatabase } from "@app/types";
import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, ButtonCreate, ButtonLink } from "../button";
import { Code } from "../code";
import { costColDef } from "../cost-estimate-tooltip";
import { DataTable, idColDef } from "../datatable";
import {
  CurrencyCell,
  DatabaseCell,
  DateTimeCell,
  EnvironmentCell,
} from "../datatable/cells";
import type {
  DataTableCellRendererProps,
  DataTableColDef,
} from "../datatable/datatable";
import { Group } from "../group";
import { IconCheck, IconPlusCircle, IconX } from "../icons";
import { OpStatus } from "../operation-status";
import { TitleBar } from "../resource-list-view";
import { ScaleRecsView } from "../scale-recs";
import { Td } from "../table";
import { tokens } from "../tokens";
import { Tooltip } from "../tooltip";
import { PitrStatusPopover } from "./pitr-status-popover";

export const DatabaseItemView = ({
  database,
}: { database: DeployDatabase }) => {
  const image = useSelector((s) =>
    selectDatabaseImageById(s, { id: database.databaseImageId }),
  );
  useQuery(fetchDatabaseImageById({ id: database.databaseImageId }));

  return (
    <div className="flex">
      <Link to={databaseDetailUrl(database.id)} className="flex">
        <img
          src={`/database-types/logo-${database.type}.png`}
          className="w-[32px] h-[32px] mr-2 mt-1 align-middle"
          aria-label={`${database.type} Database`}
        />
        <p className="flex flex-col">
          <span className={tokens.type["table link"]}>{database.handle}</span>
          <span className={tokens.type["normal lighter"]}>
            {image.description}
          </span>
        </p>
      </Link>
    </div>
  );
};

export const LastOpCell = ({ database }: { database: DeployDatabase }) => {
  const lastOperation = useSelector((s) =>
    selectLatestOpByDatabaseId(s, { dbId: database.id }),
  );
  return (
    <Td className="2xl:flex-cell-md sm:flex-cell-sm">
      <div className={tokens.type.darker}>
        <Link
          to={operationDetailUrl(lastOperation.id)}
          className={tokens.type["table link"]}
        >
          {capitalize(lastOperation.type)} by {lastOperation.userName}
        </Link>
      </div>
      <div className={tokens.type.darker} />
      <div className={tokens.type["normal lighter"]}>
        <OpStatus status={lastOperation.status} />{" "}
        {prettyDateTime(lastOperation.createdAt)}
      </div>
    </Td>
  );
};

// ─── Shared column definitions ────────────────────────────────────────────────

const colDefs: Record<string, DataTableColDef<DeployDatabaseRow>> = {
  handle: {
    field: "handle",
    headerName: "Handle",
    initialWidth: 200,
    suppressSizeToFit: true,
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployDatabaseRow>) => (
      <DatabaseCell id={data.id} />
    ),
  },
  id: {
    field: "id",
    cellDataType: "number",
    ...idColDef,
    valueGetter: ({ data }) => Number.parseInt(data?.id || "0", 10),
    sort: "desc",
  },
  environment: {
    field: "envHandle",
    headerName: "Environment",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployDatabaseRow>) => (
      <EnvironmentCell id={data.environmentId} hideIcon={true} />
    ),
  },
  diskSize: {
    field: "diskSize",
    headerName: "Disk Size",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployDatabaseRow>) => (
      <span>{data.diskSize} GB</span>
    ),
  },
  containerSize: {
    field: "containerSize",
    headerName: "Container Size",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployDatabaseRow>) => (
      <span>
        {data.containerSize} GB · {data.containerCpu} CPU
      </span>
    ),
  },
  backups: {
    field: "enableBackups",
    headerName: "Backups",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployDatabaseRow>) => {
      const image = useSelector((s) =>
        selectDatabaseImageById(s, { id: data.databaseImageId }),
      );
      const pitrIconColor =
        hasDeployDatabaseImage(image) && !image.pitrSupported
          ? "#6B7280"
          : "#AD1A1A";
      return (
        <div className="flex flex-col gap-1 text-sm">
          <div>{data.enableBackups ? "Enabled" : "Disabled"}</div>
          {data.type === "postgresql" && (
            <div className="flex items-center gap-1">
              {data.enablePitr ? (
                <>
                  <IconCheck variant="sm" color="#00633F" />
                  <span>PITR</span>
                </>
              ) : (
                <>
                  <IconX variant="sm" color={pitrIconColor} />
                  <PitrStatusPopover
                    database={data}
                    image={image}
                    trigger={
                      <span className="underline cursor-pointer">PITR</span>
                    }
                  />
                </>
              )}
            </div>
          )}
        </div>
      );
    },
  },
  savings: {
    field: "savings",
    headerName: "Container Efficiency",
    initialWidth: 220,
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployDatabaseRow>) => {
      const service = useSelector((s) =>
        selectServiceById(s, { id: data.serviceId }),
      );
      return <ScaleRecsView service={service} />;
    },
  },
  scaleRecommendation: {
    field: "scaleRec",
    headerName: "Scale Recommendation",
    valueGetter: ({ data }) =>
      data?.scaleRec === "none" ? null : data?.scaleRec,
    hide: true,
  },
  createdAt: {
    field: "createdAt",
    headerName: "Created At (UTC)",
    initialHide: true,
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployDatabaseRow>) => (
      <DateTimeCell date={data.createdAt} />
    ),
  },
  cost: {
    field: "cost",
    ...costColDef,
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployDatabaseRow>) => (
      <CurrencyCell value={data.cost} />
    ),
  },
  actions: {
    colId: "actions",
    headerName: "Actions",
    sortable: false,
    width: 90,
    resizable: false,
    suppressAutoSize: true,
    suppressSizeToFit: true,
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployDatabaseRow>) => (
      <Link
        to={databaseScaleUrl(data.id)}
        className="hover:no-underline flex justify-end mr-4"
      >
        <Button variant="primary" size="sm">
          Scale
        </Button>
      </Link>
    ),
  },
};

// ─── DatabaseListByOrg ────────────────────────────────────────────────────────

const dbByOrgColDefs = [
  colDefs.handle,
  colDefs.id,
  colDefs.environment,
  colDefs.diskSize,
  colDefs.containerSize,
  colDefs.backups,
  colDefs.createdAt,
  colDefs.savings,
  colDefs.scaleRecommendation,
  colDefs.cost,
  colDefs.actions,
];

export const DatabaseListByOrg = () => {
  const orgId = useSelector(selectOrganizationSelectedId);
  const { isLoading, isInitialLoading } = useCompositeLoader([
    fetchDatabases,
    fetchServices,
    fetchEnvironments,
    fetchDatabaseImages,
    fetchCostsByDatabases({ orgId }),
    fetchManualScaleRecommendations,
  ]);
  const dbs = useSelector((s) => selectDatabasesForTable(s, {}));

  return (
    <Group size="xl">
      <Group variant="horizontal" className="items-center justify-between">
        <TitleBar description="Databases provide data persistence and are automatically configured and managed.">
          Databases
        </TitleBar>
        <ButtonLink to={createDbUrl()}>
          <IconPlusCircle variant="sm" className="mr-2" /> New Database
        </ButtonLink>
      </Group>

      <DataTable<DeployDatabaseRow>
        rowData={dbs}
        colDefs={dbByOrgColDefs}
        syncQueryParams={true}
        isLoading={!dbs.length && (isLoading || isInitialLoading)}
      />
    </Group>
  );
};

// ─── DatabaseListByEnvironment ────────────────────────────────────────────────

const dbByEnvironmentColDefs = [
  colDefs.handle,
  colDefs.id,
  colDefs.diskSize,
  colDefs.containerSize,
  colDefs.backups,
  colDefs.cost,
  colDefs.savings,
  colDefs.scaleRecommendation,
  colDefs.actions,
];

export const DatabaseListByEnvironment = ({
  envId,
}: {
  envId: string;
}) => {
  const orgId = useSelector(selectOrganizationSelectedId);
  useQuery(fetchCostsByDatabases({ orgId }));
  useQuery(fetchEnvironmentById({ id: envId }));
  const navigate = useNavigate();
  const onCreate = () => {
    navigate(environmentCreateDbUrl(envId));
  };
  const dbs = useSelector((s) => selectDatabasesByEnvIdForTable(s, { envId }));

  return (
    <Group size="xl">
      <div className="flex justify-end">
        <ButtonCreate envId={envId} onClick={onCreate}>
          <IconPlusCircle variant="sm" className="mr-2" />
          New Database
        </ButtonCreate>
      </div>

      <DataTable<DeployDatabaseRow>
        rowData={dbs}
        colDefs={dbByEnvironmentColDefs}
      />
    </Group>
  );
};

// ─── DatabaseDependencyList ───────────────────────────────────────────────────

type DatabaseDependencyRow = DeployDatabaseRow & { why?: string };

const dbDependencyColDefs: DataTableColDef<DatabaseDependencyRow>[] = [
  colDefs.handle,
  colDefs.id,
  colDefs.environment,
  colDefs.diskSize,
  colDefs.containerSize,
  colDefs.cost,
  {
    field: "why",
    headerName: "Reason",
    cellRenderer: ({
      data,
    }: DataTableCellRendererProps<DatabaseDependencyRow>) =>
      data.why ? (
        <Tooltip placement="left" text={data.why} fluid>
          <Code>{data.why}</Code>
        </Tooltip>
      ) : null,
  },
];

export const DatabaseDependencyList = ({
  databases,
}: {
  databases: DatabaseDependency[];
}) => {
  const allTableDbs = useSelector((s) => selectDatabasesForTable(s, {}));

  const rows = useMemo<DatabaseDependencyRow[]>(
    () =>
      databases.flatMap((dep) => {
        const row = allTableDbs.find((r) => r.id === dep.resource.id);
        if (!row) return [];
        return [{ ...row, why: dep.why }];
      }),
    [databases, allTableDbs],
  );

  return (
    <DataTable<DatabaseDependencyRow>
      rowData={rows}
      colDefs={dbDependencyColDefs}
      pageSize={25}
    />
  );
};
